import { useEffect, useState } from "react";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import { formatDateTime, formatNumber, formatPercent, formatUsd } from "../format";
import useApiQuery from "../useApiQuery";
import { PAYOUT_KINDS } from "./PayoutsPage";
import styles from "./OverviewPage.module.css";

const WITHDRAWAL_STATUS = {
  Pending: { label: "Pending", tone: "warning" },
  Paid: { label: "Paid", tone: "success" },
  Rejected: { label: "Rejected", tone: "danger" },
};

// 0x5aAe…eAed
const shortWallet = (address) => (address ? `${address.slice(0, 6)}…${address.slice(-4)}` : "—");

// Figures from GET /api/admin/overview; anything it returns as null (no data source
// yet: funded accounts, activation revenue) shows as a dash
// "+1%": the qualifying target the API applies (balance ≥ deposit × (1 + target))
const targetLabel = (pct) => `${pct ?? 1}%`;

function platformStats({ platform, qualifyingTargetPct }) {
  return [
    {
      label: "Free active accounts",
      value: formatNumber(platform.freeActive),
      note: `${formatNumber(platform.startedToday)} started today`,
    },
    { label: "Awaiting activation", value: formatNumber(platform.awaitingActivation), note: `Reached ${targetLabel(qualifyingTargetPct)} · Not activated` },
    { label: "Active funded accounts", value: formatNumber(platform.activeFunded) },
    { label: "Total USDC paid", value: formatUsd(platform.totalUsdcPaid), note: "Qualification + withdrawals" },
    { label: "Pending withdrawals", value: formatNumber(platform.pendingWithdrawals) },
    { label: "Open support tickets", value: formatNumber(platform.openSupportTickets) },
  ];
}

function actionItems({ actions }) {
  return [
    {
      label: "Withdrawal requests",
      count: formatNumber(actions.withdrawalRequests),
      tone: "danger",
      action: "Review",
      path: "/withdrawals/payouts",
    },
    {
      label: "Open support tickets > 24h",
      count: formatNumber(actions.staleSupportTickets),
      tone: "warning",
      action: "Open",
      path: "/support",
    },
    {
      label: "Reward requests",
      count: formatNumber(actions.rewardRequests),
      tone: "warning",
      action: "Review",
      path: "/withdrawals/rewards",
    },
  ];
}

function activityGroups({ today, qualifyingTargetPct }) {
  const { qualification, trading, payments } = today;
  return [
    {
      group: "Qualification",
      rows: [
        { label: "Free account started", value: formatNumber(qualification.freeStarted) },
        { label: `Traders reaching +${targetLabel(qualifyingTargetPct)}`, value: formatNumber(qualification.reachedTarget) },
        { label: "KYC completed", value: formatNumber(qualification.kycCompleted) },
        { label: "Paid activations", value: formatNumber(qualification.paidActivations) },
        { label: "Free-to-paid conversion", value: formatPercent(qualification.conversion) },
      ],
    },
    {
      group: "Trading",
      rows: [
        { label: "Active traders today", value: formatNumber(trading.activeTraders) },
        { label: "Total lots traded", value: formatNumber(trading.lotsTraded, 1) },
        { label: "Accounts in drawdown", value: formatNumber(trading.inDrawdown) },
        { label: "Daily-loss breaches", value: formatNumber(trading.dailyLossBreaches), tone: "warning" },
        { label: "Maximum loss breaches", value: formatNumber(trading.maxLossBreaches), tone: "danger" },
      ],
    },
    {
      group: "Payments",
      rows: [
        { label: "Activation revenue", value: formatUsd(payments.activationRevenue) },
        { label: "USDC payouts today", value: formatUsd(payments.usdcPayouts) },
        { label: "Referral rewards paid", value: formatUsd(payments.referralRewardsPaid) },
        { label: "Failed payments", value: formatNumber(payments.failedPayments), tone: "warning" },
      ],
    },
  ];
}

function minutesAgo(date, now) {
  const minutes = Math.max(0, Math.round((now - new Date(date)) / 60000));
  return minutes === 0 ? "Updated just now" : `Updated ${minutes} min ago`;
}

export default function OverviewPage({ onNavigate }) {
  const { data, status, reload } = useApiQuery("/api/admin/overview");
  // Re-render each minute so "Updated N min ago" stays true
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  if (!data) {
    return (
      <section className={styles.page}>
        <header className={styles.pageHeader}>
          <h1 className={styles.title}>Overview</h1>
        </header>
        <p className={styles.meta}>
          {status === "error" ? (
            <>
              We couldn't load the overview.{" "}
              <TextLink arrow={false} onClick={reload}>
                Try again
              </TextLink>
            </>
          ) : (
            "Loading…"
          )}
        </p>
      </section>
    );
  }

  const withdrawals = data.withdrawals;

  return (
    <section className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.title}>Overview</h1>
        <Tag dot>Live</Tag>
      </header>

      <section className={styles.section} aria-labelledby="platform-overview">
        <SectionHeader title={<span id="platform-overview">Platform overview</span>} />
        <StatGrid>
          {platformStats(data).map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </StatGrid>
      </section>

      <section className={styles.section}>
        <SectionHeader title="Action required" />
        <div className={styles.actionGrid}>
          {actionItems(data).map((item) => (
            <article key={item.label} className={styles.actionCard}>
              <p className={`${styles.cardLabel} label-font`}>{item.label}</p>
              <div className="d-flex align-items-end justify-content-between gap-1">
                <p className={`${styles.actionCount} d-flex align-items-center`}>
                  <span
                    className={`${styles.dot} ${styles[`dot${item.tone[0].toUpperCase()}${item.tone.slice(1)}`]}`}
                    aria-hidden="true"
                  />
                  {item.count}
                </p>
                <TextLink onClick={() => onNavigate(item.path)}>{item.action}</TextLink>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <SectionHeader title="Activation funnel" />
        <ol className={styles.funnel}>
          {data.funnel.map((step) => (
            <li key={step.label} className={styles.funnelStep}>
              <p className={styles.funnelCount}>{formatNumber(step.count)}</p>
              <p className={styles.funnelLabel}>{step.label}</p>
              <p className={styles.funnelRate}>{formatPercent(step.rate)}</p>
            </li>
          ))}
        </ol>
        <div className={`${styles.conversion} d-flex align-items-center justify-content-between`}>
          <p className={`${styles.conversionLabel} label-font`}>Free → paid conversion</p>
          <p className={styles.conversionValue}>{formatPercent(data.conversion)}</p>
        </div>
      </section>

      <section className={styles.section}>
        <SectionHeader
          title="Today's activity"
          aside={
            <button type="button" className={`${styles.meta} ${styles.refresh}`} onClick={reload}>
              {status === "loading" ? "Updating…" : minutesAgo(data.generatedAt, now)}
            </button>
          }
        />
        {activityGroups(data).map(({ group, rows }) => (
          <div key={group} className={styles.activityGroup}>
            <h3 className={`${styles.groupTitle} label-font`}>{group}</h3>
            <dl className={styles.activityList}>
              {rows.map((row) => (
                <div key={row.label} className={`${styles.activityRow} d-flex justify-content-between`}>
                  <dt>{row.label}</dt>
                  <dd className={row.tone ? styles[`value${row.tone[0].toUpperCase()}${row.tone.slice(1)}`] : ""}>
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </section>

      <section className={styles.section}>
        <SectionHeader
          title="Withdrawals"
          aside={
            <TextLink className={styles.largeLink} onClick={() => onNavigate("/withdrawals/payouts")}>
              Open queue
            </TextLink>
          }
        />
        <p className={`${styles.withdrawalTotal} d-flex align-items-end flex-wrap`}>
          {formatUsd(withdrawals?.pendingTotal)}
          <span className={styles.meta}>{formatNumber(withdrawals?.pendingCount)} requests pending</span>
        </p>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${styles.withdrawalTable}`}>
            <thead>
              <tr>
                {["Trader", "PRP", "USDC", "Type", "Wallet", "Requested", "Status"].map((heading) => (
                  <th key={heading} scope="col">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(withdrawals?.rows ?? []).map((row) => {
                const status = WITHDRAWAL_STATUS[row.status];

                return (
                  <tr key={row.id}>
                    <td>{row.trader ?? "—"}</td>
                    <td>{formatNumber(row.amountPrp)}</td>
                    <td>{formatNumber(row.amountUsdc, 2)}</td>
                    <td>{PAYOUT_KINDS[row.kind]}</td>
                    <td title={row.address}>{shortWallet(row.address)}</td>
                    <td>{formatDateTime(row.requestedAt)}</td>
                    <td>
                      <Tag tone={status.tone}>{status.label}</Tag>
                    </td>
                  </tr>
                );
              })}
              {withdrawals?.rows.length ? null : (
                <tr>
                  <td colSpan={7} className={controlStyles.empty}>
                    No payouts waiting for review.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
